import { useEffect } from 'react';
import { useSyncExternalStore } from 'react';
import * as api from '../api';
import { getMyLocation } from './geo';
import { useActiveMemberId } from './activeMember';
import { queryClient, queryKeys } from './queries';
import { toast } from './ui';

/**
 * "Share my location while Pacer is open." Per-device (localStorage), opt-in, and honest about
 * the web's limits: it only updates while the app is foregrounded — there is no background
 * tracking on iOS. Tied to whoever is tapped as "you" on this device.
 */

const KEY = 'pacer.shareLocation';
const INTERVAL_MS = 3 * 60 * 1000;

let on = read();
const listeners = new Set<() => void>();

function read(): boolean {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
}

function emit(): void {
  for (const l of listeners) l();
}

export function setLocationSharing(v: boolean): void {
  on = v;
  try {
    if (v) localStorage.setItem(KEY, '1');
    else localStorage.removeItem(KEY);
  } catch {
    /* non-persistent is still usable this session */
  }
  emit();
}

export function useLocationSharingEnabled(): boolean {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => on,
    () => false,
  );
}

/**
 * Mounted once at the app root. While enabled + authenticated + a member is chosen, it pushes
 * that member's position on open, on every return to the foreground, and every few minutes.
 * A permission denial turns sharing off rather than nagging on each tick.
 */
export function useShareLocationWhileOpen(active: boolean): void {
  const enabled = useLocationSharingEnabled();
  const memberId = useActiveMemberId();

  useEffect(() => {
    if (!active || !enabled || !memberId) return;
    let cancelled = false;

    const push = async (): Promise<void> => {
      if (document.visibilityState !== 'visible') return;
      try {
        const { lat, lon } = await getMyLocation();
        if (cancelled) return;
        await api.setLocation(memberId, lat, lon);
        void queryClient.invalidateQueries({ queryKey: queryKeys.locations });
      } catch (e) {
        if (cancelled) return;
        setLocationSharing(false);
        toast(e instanceof Error ? e.message : 'Location sharing turned off.', 'error');
      }
    };

    void push();
    const iv = setInterval(() => void push(), INTERVAL_MS);
    const onVis = (): void => {
      if (document.visibilityState === 'visible') void push();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [active, enabled, memberId]);
}
